import { useState, useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client/react'
import CREATE_PROGRAMME_PREFILLED from '../../../../../graphql/mutations/courseCatalogue/createProgrammePrefilled'
import useUser from '../../../../../hooks/useUser'

import { useLocation, useNavigate, Link } from 'react-router-dom'

import { Form, Container, Row, Col, ListGroup } from "react-bootstrap"
import {
    faUpload,
    faChevronLeft
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import CustomButton from '../../../../../components/CustomButton'

import COLORS from '../../../../../data/colors'
import './index.scss'

import Spinner from "react-bootstrap/Spinner"

import { 
  capitalizeFirstLetter
} from '../../../../../utils/courseCatalogueManager'


const UploadProgramme = () => {
    
  const { user } = useUser()

  const navigate = useNavigate()

  const formRef = useRef(null)

  const [file, setFile] = useState(null)
  const [selectedFileError, setSelectedFileError] = useState([])
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadResults, setUploadResults] = useState({})
  const [uploadHasError, setUploadHasError] = useState(false)

  const [validated, setValidated] = useState(false)

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [createProgrammePrefilled, { 
      loading: loadingCreateProgrammePrefilled, 
      error: errorCreateProgrammePrefilled, 
      data: dataCreateProgrammePrefilled
    }] = useMutation(
    CREATE_PROGRAMME_PREFILLED, {
      onCompleted: (data) => {
        console.log("CSV uploaded")
        setUploadResults(data.createProgrammePrefilled)

        if(data?.createProgrammePrefilled?.success) {
          setUploadSuccess(true)
          setUploadHasError(false)
          setValidated(false)
        } else {
          setUploadHasError(true)
          setUploadSuccess(false)
          setValidated(false)
        }
      },
      onError: (error) => {
        console.error("Error CSV upload")
      }
    }
  )

  useEffect(() => {
    
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))

    if(!userHasPermission) {
      navigate('/cc')
      return
    }

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

  }, [])

  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) {
      setSelectedFileError(["CSV file is required"])
      setFile(null)
    } else if (!selectedFile.name.endsWith(".csv")) {
      setSelectedFileError(["Only .csv files are allowed"])
      setFile(null)
    } else {
      setSelectedFileError([])
      setFile(selectedFile)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadSuccess(false)
    setUploadHasError(false)

    if (!formIsValidated(e)) return

    if (!file) {
      setSelectedFileError(["CSV file is required"])
      return
    }

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    try {
      const arrayBuffer = await file.arrayBuffer()
      let binary = ''
      const bytes = new Uint8Array(arrayBuffer)
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      const fileBase64 = btoa(binary)

      let result = await createProgrammePrefilled({
        variables: {
          programmeFile: fileBase64,
          baseUrl,
          apiKey,
          schacCode: user?.heiID,
        },
      })

      if(uploadResults?.success) {
        setUploadSuccess(true)
        setUploadHasError(false)
        setValidated(false)
      } else {
        setUploadHasError(true)
        setUploadSuccess(false)
        setValidated(false)
      }
    } catch (err) {
      console.error("Upload failed")
      setValidated(false)
    }
  }

  const isFormReady = Boolean(file)

  return (
    <Container className="mt-5 pt-3">
      <Row className="mb-5">
        <Link to="/cc/programmes" className="goBackLink">
          <FontAwesomeIcon
            icon={faChevronLeft}
            color={COLORS.greyText}
            className="with-icon me-1"
            id={"smallIcon"}
          />
          {"Go back"}
        </Link>
      </Row>
      <Row className="align-items-center">
        <Col>
          <Form 
            ref={formRef} 
            noValidate 
            validated={validated} 
            id={"upload-programme-form"}
            onSubmit={handleSubmit}
          >
            <h3 className='h3-upload mb-5'>
              {"Upload a CSV file to create or update multiple Programmes."}
            </h3>
            
            <p className='lh-base mb-4'>
              {"You can find spreadsheet templates and documentation on how to upload your course and programme data "}
              <a href="https://templates.cc.uni-foundation.eu/" target="_blank" rel="noreferrer">{"here"}</a>.
            </p>

            <Form.Group controlId="formFile" className="my-3">
              <Form.Control type="file" accept='.csv' onChange={handleFileChange} required />
              <Form.Control.Feedback type="invalid">
                {selectedFileError.map((msg, i) => (
                  <div key={i} className="text-danger">
                    {msg}
                  </div>
                ))}
              </Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col style={{ marginTop: 36, display: "flex", justifyContent: "flex-end" }}>
                <CustomButton 
                  buttonType={"primary"} 
                  small={true} 
                  text={loadingCreateProgrammePrefilled ? "uploading..." : "upload"} 
                  hasIcon={true} 
                  iconColor={COLORS.white} 
                  icon={faUpload} 
                  disabled={!isFormReady}
                  handleClick={() => formRef.current?.requestSubmit()}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      {loadingCreateProgrammePrefilled &&
        <Row className="align-items-center mt-5">
          <div className="text-center">
            <Spinner
              as="div"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="align-middle my-auto me-2"
            />
            <span>{'Uploading CSV file'}</span>
          </div>
        </Row>
      }

      {selectedFileError.length > 0 &&
        (
          <Row className="mt-5 align-items-center">
            <p className="text-danger text-center">
              {selectedFileError}
            </p>
          </Row>
        )
      }
      
      {errorCreateProgrammePrefilled && 
        (
          <Row className="mt-5 align-items-center">
            <p className="text-danger text-center">
              {capitalizeFirstLetter(errorCreateProgrammePrefilled?.message)}
            </p>
          </Row>
        )
      }

      {uploadHasError && uploadResults?.errors &&
        (
          <Row className="my-5 align-items-center">
            <Col md={10} className="p-4 text-center mx-auto">
              <p className="mb-4 lead">{"The CSV contains validation error(s)"}</p>
              <ListGroup>
                {uploadResults?.errors.map((err, i) => (
                  <ListGroup.Item key={i}>
                  <p className="my-3">
                    {err.row && (
                      <>
                        <span className="fw-bold">{`Row ${err.row}`}</span>:
                      </>
                    )}
                    <span className="ms-2 me-2">{err.message}</span>
                    <span className="fw-bold">Column</span>:
                    <span className="ms-2">{err.path}</span>
                  </p>
                </ListGroup.Item>
                ))}
              </ListGroup>
            </Col>
          </Row>
        )
      }

      {(uploadSuccess || dataCreateProgrammePrefilled?.createProgrammePrefilled?.success) && !loadingCreateProgrammePrefilled &&
        <Row className="align-items-center mt-5">
          <Col>
            <p className="text-success text-center">{"The CSV was succefully uploaded and the Programmes(s) were created. You can check them in the list page."}</p>
          </Col>
        </Row>
      }
    </Container>
  )
}

export default UploadProgramme